import { Menu, LogOut, ChevronDown, ChevronLeft, Sun, Moon, Search, Bell, User, Store, ExternalLink, Globe, CircleUser, Gift } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useStore } from '../../contexts/StoreContext';
import { changeLanguage } from '../../i18n';
import { ACC_URL } from '../../lib/constants';
import { ordersApi } from '../../api/endpoints/orders';
import { MyAccountModal } from '../account/MyAccountModal';

interface TopBarProps {
  onMenuToggle: () => void;
}

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface SearchItem {
  id: string;
  label: string;
  path: string;
  parent?: string;
  shortcut?: string;
}

export function TopBar({ onMenuToggle }: TopBarProps) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { storeId } = useParams();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (showSearch) setTimeout(() => searchInputRef.current?.focus(), 50);
  }, [showSearch]);

  const { currentStore } = useStore();

  const toggleLang = () => {
    const newLang = i18n.language === 'ar' ? 'en' : 'ar';
    changeLanguage(newLang);
  };

  const storeUrl = currentStore?.domain
    ? `https://${currentStore.domain}`
    : storeId ? `https://${storeId}.00stores.com` : null;

  // Unseen orders count for notification bell
  const { data: unseenCount } = useQuery({
    queryKey: ['orders-unseen', storeId],
    queryFn: () => ordersApi.unseenCount(storeId!),
    enabled: !!storeId,
    refetchInterval: 30000,
  });

  // --- Breadcrumbs ---
  const breadcrumbs = useMemo((): BreadcrumbItem[] => {
    if (!storeId) return [];
    const path = location.pathname.replace('/dashboard/', '').replace('/dashboard', '');
    const segments = path.split('/').filter(Boolean);
    if (segments[0] === storeId) segments.shift();
    if (segments.length === 0) return [{ label: t('sidebar.home') }];

    const ROUTE_LABELS: Record<string, string> = {
      orders: t('sidebar.orders'), products: t('sidebar.products'), customers: t('sidebar.customers'),
      wallet: t('sidebar.wallet'), analytics: t('sidebar.analytics'), marketing: t('sidebar.marketing'),
      coupons: t('sidebar.coupons'), reviews: t('sidebar.reviews'), settings: t('sidebar.storeSettings'),
      shipping: t('sidebar.shipping'), pages: t('sidebar.pages'), apps: t('sidebar.apps'),
      users: t('sidebar.team'), inventory: t('sidebar.inventory'), categories: t('sidebar.categories'),
      translations: t('sidebar.translations'), paylinks: t('sidebar.paylinks'), carts: t('sidebar.carts'),
      'analytical-links': t('sidebar.analyticalLinks'), newsletter: t('sidebar.newsletter'),
      contacts: t('contactInbox.title'), complaints: t('complaints.title'), create: t('common.create'),
    };

    const PARENTS: Record<string, { label: string; path: string }> = {
      categories: { label: t('sidebar.products'), path: `/${storeId}/products` },
      inventory: { label: t('sidebar.products'), path: `/${storeId}/products` },
      translations: { label: t('sidebar.products'), path: `/${storeId}/products` },
      coupons: { label: t('sidebar.marketing'), path: `/${storeId}/marketing` },
      reviews: { label: t('sidebar.marketing'), path: `/${storeId}/marketing` },
      'analytical-links': { label: t('sidebar.marketing'), path: `/${storeId}/marketing` },
      carts: { label: t('sidebar.marketing'), path: `/${storeId}/marketing` },
      shipping: { label: t('sidebar.storeSettings'), path: `/${storeId}/settings` },
      pages: { label: t('sidebar.storeSettings'), path: `/${storeId}/settings` },
      apps: { label: t('sidebar.storeSettings'), path: `/${storeId}/settings` },
      users: { label: t('sidebar.storeSettings'), path: `/${storeId}/settings` },
    };

    const items: BreadcrumbItem[] = [];
    const parent = PARENTS[segments[0]];
    if (parent) items.push({ label: parent.label, path: parent.path });

    segments.forEach((seg, i) => {
      const label = ROUTE_LABELS[seg];
      if (label) {
        items.push({ label, path: i === segments.length - 1 ? undefined : `/${storeId}/${segments.slice(0, i + 1).join('/')}` });
      } else if (seg !== 'create') {
        items.push({ label: `#${seg.slice(-6)}` });
      }
    });
    return items;
  }, [location.pathname, storeId, t]);

  // --- Search items ---
  const searchItems = useMemo((): SearchItem[] => {
    if (!storeId) return [];
    return [
      { id: 'home', label: t('sidebar.home'), path: `/${storeId}`, shortcut: '1' },
      { id: 'orders', label: t('sidebar.orders'), path: `/${storeId}/orders`, shortcut: '2' },
      { id: 'orders-create', label: t('orders.addOrder'), path: `/${storeId}/orders/create`, parent: t('sidebar.orders') },
      { id: 'products', label: t('sidebar.products'), path: `/${storeId}/products`, shortcut: '3' },
      { id: 'products-create', label: t('products.addProduct'), path: `/${storeId}/products/create`, parent: t('sidebar.products') },
      { id: 'categories', label: t('sidebar.categories'), path: `/${storeId}/categories`, parent: t('sidebar.products') },
      { id: 'inventory', label: t('sidebar.inventory'), path: `/${storeId}/inventory`, parent: t('sidebar.products') },
      { id: 'translations', label: t('sidebar.translations'), path: `/${storeId}/translations`, parent: t('sidebar.products') },
      { id: 'customers', label: t('sidebar.customers'), path: `/${storeId}/customers`, shortcut: '4' },
      { id: 'wallet', label: t('sidebar.wallet'), path: `/${storeId}/wallet`, shortcut: '5' },
      { id: 'analytics', label: t('sidebar.analytics'), path: `/${storeId}/analytics`, shortcut: '6' },
      { id: 'marketing', label: t('sidebar.marketing'), path: `/${storeId}/marketing`, shortcut: '7' },
      { id: 'coupons', label: t('sidebar.coupons'), path: `/${storeId}/coupons`, parent: t('sidebar.marketing') },
      { id: 'coupons-create', label: t('coupons.addCoupon'), path: `/${storeId}/coupons/create`, parent: t('sidebar.coupons') },
      { id: 'reviews', label: t('sidebar.reviews'), path: `/${storeId}/reviews`, parent: t('sidebar.marketing') },
      { id: 'analytical-links', label: t('sidebar.analyticalLinks'), path: `/${storeId}/analytical-links`, parent: t('sidebar.marketing') },
      { id: 'carts', label: t('sidebar.carts'), path: `/${storeId}/carts`, parent: t('sidebar.marketing') },
      { id: 'settings', label: t('sidebar.storeSettings'), path: `/${storeId}/settings`, shortcut: '8' },
      { id: 'settings-general', label: t('settings.general'), path: `/${storeId}/settings?tab=general`, parent: t('sidebar.storeSettings') },
      { id: 'settings-domain', label: t('settings.domain'), path: `/${storeId}/settings?tab=domain`, parent: t('sidebar.storeSettings') },
      { id: 'settings-payment', label: t('settings.paymentMethods'), path: `/${storeId}/settings?tab=paymentMethods`, parent: t('sidebar.storeSettings') },
      { id: 'settings-notifications', label: t('settings.notifications'), path: `/${storeId}/settings?tab=notifications`, parent: t('sidebar.storeSettings') },
      { id: 'settings-email', label: t('settings.emailBranding', 'Email branding'), path: `/${storeId}/settings?tab=emailBranding`, parent: t('sidebar.storeSettings') },
      { id: 'settings-auth', label: t('settings.clientAuth'), path: `/${storeId}/settings?tab=auth`, parent: t('sidebar.storeSettings') },
      { id: 'settings-api', label: t('settings.apiKeys'), path: `/${storeId}/settings?tab=apiKeys`, parent: t('sidebar.storeSettings') },
      { id: 'settings-ai', label: t('settingsAiConnectors.tab', 'AI connectors'), path: `/${storeId}/settings?tab=aiConnectors`, parent: t('sidebar.storeSettings') },
      { id: 'settings-webhooks', label: t('settingsWebhooks.title'), path: `/${storeId}/settings?tab=webhooks`, parent: t('sidebar.storeSettings') },
      { id: 'settings-tracking', label: t('settingsTracking.title'), path: `/${storeId}/settings?tab=tracking`, parent: t('sidebar.storeSettings') },
      { id: 'settings-welcome', label: t('settingsWelcome.tab', 'Welcome'), path: `/${storeId}/settings?tab=welcome`, parent: t('sidebar.storeSettings') },
      { id: 'settings-help', label: t('settingsHelp.tab', 'Help'), path: `/${storeId}/settings?tab=help`, parent: t('sidebar.storeSettings') },
      { id: 'shipping', label: t('sidebar.shipping'), path: `/${storeId}/shipping`, parent: t('sidebar.storeSettings') },
      { id: 'pages', label: t('sidebar.pages'), path: `/${storeId}/pages`, parent: t('sidebar.storeSettings') },
      { id: 'apps', label: t('sidebar.apps'), path: `/${storeId}/apps`, parent: t('sidebar.storeSettings') },
      { id: 'team', label: t('sidebar.team'), path: `/${storeId}/users`, parent: t('sidebar.storeSettings') },
      { id: 'paylinks', label: t('sidebar.paylinks'), path: `/${storeId}/paylinks` },
      { id: 'paylinks-create', label: t('paylinks.addPaylink'), path: `/${storeId}/paylinks/create`, parent: t('sidebar.paylinks') },
    ];
  }, [storeId, t]);

  // Ctrl+K and Ctrl+1-9 shortcuts (must be after searchItems)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
        setSearchQuery('');
        return;
      }
      if (e.key === 'Escape') {
        setShowSearch(false);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key >= '1' && e.key <= '9' && storeId) {
        const item = searchItems.find(si => si.shortcut === e.key);
        if (item) {
          e.preventDefault();
          navigate(item.path);
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [storeId, searchItems, navigate]);

  const filteredSearch = useMemo(() => {
    if (!searchQuery.trim()) return searchItems;
    const q = searchQuery.toLowerCase();
    return searchItems.filter(item => item.label.toLowerCase().includes(q) || item.parent?.toLowerCase().includes(q));
  }, [searchQuery, searchItems]);

  const handleSearchSelect = useCallback((item: SearchItem) => {
    navigate(item.path);
    setShowSearch(false);
    setSearchQuery('');
  }, [navigate]);

  const handleSearchSubmit = useCallback(() => {
    const q = searchQuery.trim();
    if (!q || !storeId) return;
    if (filteredSearch.length > 0) {
      handleSearchSelect(filteredSearch[0]);
    } else {
      // No page match - search products
      navigate(`/${storeId}/products?q=${encodeURIComponent(q)}`);
      setShowSearch(false);
      setSearchQuery('');
    }
  }, [searchQuery, storeId, filteredSearch, handleSearchSelect, navigate]);

  const roleLabel = (() => {
    const r = (currentStore as any)?.role ?? null;
    // No role yet (still loading) -> show nothing rather than falsely claiming
    // ownership. Known roles (owner/admin/staff/...) are translated via the
    // roles namespace; custom role names fall back to the raw string.
    if (!r) return '';
    const name: string = typeof r === 'string' ? r : r.name;
    if (!name) return '';
    return t(`roles.${name}`, { defaultValue: name });
  })();

  return (
    <>
      <header className="ui-topbar sticky top-0 z-30 flex h-14 2xl:h-16 items-center justify-between px-5 2xl:px-7 relative">
        {/* Left - mobile menu + breadcrumbs */}
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={onMenuToggle}
            className="ui-icon-btn lg:hidden"
            style={{ width: 36, height: 36 }}
          >
            <Menu className="h-5 w-5" />
          </button>

          <nav className="hidden lg:flex items-center gap-1.5 text-sm 2xl:text-[15px]">
            {breadcrumbs.map((crumb, i) => (
              <div key={i} className="flex items-center gap-1.5">
                {i > 0 && <ChevronLeft className="h-3.5 w-3.5 text-text-muted/50 shrink-0 rtl:rotate-180" />}
                {crumb.path ? (
                  <button
                    onClick={() => navigate(crumb.path!)}
                    className="text-text-muted hover:text-text-primary transition-colors whitespace-nowrap"
                  >
                    {crumb.label}
                  </button>
                ) : (
                  <span className="text-text-primary font-semibold whitespace-nowrap">{crumb.label}</span>
                )}
              </div>
            ))}
          </nav>
        </div>

        {/* Center - search bar (desktop: absolute center, mobile: icon only) */}
        <button
          onClick={() => { setShowSearch(true); setSearchQuery(''); }}
          className="ui-icon-btn lg:hidden"
        >
          <Search className="h-5 w-5" />
        </button>
        <div className="absolute inset-0 hidden lg:flex items-center justify-center pointer-events-none">
          <button
            onClick={() => { setShowSearch(true); setSearchQuery(''); }}
            className="pointer-events-auto flex items-center gap-3 rounded-xl border border-border-default bg-bg-tertiary/70 hover:bg-bg-tertiary px-4 py-2 2xl:px-5 2xl:py-2.5 text-sm 2xl:text-[14px] text-text-muted hover:border-border-strong hover:text-text-secondary transition-colors w-80 2xl:w-96"
          >
            <Search className="h-4 w-4 shrink-0" />
            <span className="flex-1 text-start truncate">{t('common.search', 'بحث')}…</span>
            <span className="ui-kbd">⌘K</span>
          </button>
        </div>

        {/* Right - primary CTA + icons + user */}
        <div className="flex items-center gap-2 2xl:gap-2.5">
          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="ui-icon-btn"
            title={theme === 'dark' ? t('topbar.lightMode', 'الوضع الفاتح') : t('topbar.darkMode', 'الوضع الداكن')}
            aria-label={theme === 'dark' ? t('topbar.lightMode', 'الوضع الفاتح') : t('topbar.darkMode', 'الوضع الداكن')}
          >
            {theme === 'dark' ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
          </button>

          {/* Notification bell */}
          <button
            onClick={() => storeId && navigate(`/${storeId}/orders`)}
            className="ui-icon-btn relative"
            title={t('topbar.notifications', 'الإشعارات')}
          >
            <Bell className="h-[18px] w-[18px]" />
            {unseenCount != null && unseenCount > 0 && (
              <span
                className="absolute top-1 end-1 h-2.5 w-2.5 rounded-full"
                style={{
                  background: '#C43D3D',
                  boxShadow: '0 0 0 2px var(--color-bg-topbar)',
                }}
                aria-label={String(unseenCount)}
              />
            )}
          </button>

          {/* User dropdown */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 2xl:gap-2.5 rounded-xl px-1.5 py-1 2xl:px-2 2xl:py-1.5 text-text-secondary hover:bg-bg-hover transition-colors"
            >
              <div
                className="flex h-9 w-9 items-center justify-center rounded-full"
                style={{ background: '#D2E9FE', color: '#0A2248' }}
              >
                <CircleUser className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <div className="hidden md:flex flex-col items-start leading-tight">
                <span className="text-[13px] font-semibold text-text-primary">{user?.username}</span>
                <span className="text-[10.5px] text-text-muted">{roleLabel}</span>
              </div>
              <ChevronDown className="h-3.5 w-3.5 text-text-muted hidden md:block" />
            </button>

            {showUserMenu && (
              <div className="absolute end-0 top-full mt-1.5 w-56 2xl:w-60 rounded-xl border border-border-default bg-bg-secondary shadow-lg overflow-hidden">
                {/* User info header */}
                <div className="px-4 py-3 border-b border-border-default/50">
                  <p className="text-sm 2xl:text-[15px] font-medium">{user?.username}</p>
                  <p className="text-xs 2xl:text-sm text-text-muted truncate">{user?.email}</p>
                </div>

                {/* Menu items */}
                <div className="py-1.5">
                  {/* Account — opens an in-place editor modal instead of
                      sending the user out to ${ACC_URL}/account */}
                  <button
                    type="button"
                    onClick={() => { setShowAccountModal(true); setShowUserMenu(false); }}
                    className="flex w-full items-center gap-2.5 px-4 py-2 text-sm 2xl:text-[15px] text-text-secondary hover:bg-bg-hover transition-colors"
                  >
                    <User className="h-4 w-4 2xl:h-[18px] 2xl:w-[18px]" />
                    {t('topbar.myAccount', 'حسابي')}
                  </button>

                  {/* Open store */}
                  {storeUrl && (
                    <a
                      href={storeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex w-full items-center gap-2.5 px-4 py-2 text-sm 2xl:text-[15px] text-text-secondary hover:bg-bg-hover transition-colors"
                    >
                      <ExternalLink className="h-4 w-4 2xl:h-[18px] 2xl:w-[18px]" />
                      {t('topbar.openStore', 'فتح المتجر')}
                    </a>
                  )}

                  {/* Referral program. Account-scoped, so it leaves the
                      current store context on purpose. */}
                  <button
                    onClick={() => { navigate('/referrals'); setShowUserMenu(false); }}
                    className="flex w-full items-center gap-2.5 px-4 py-2 text-sm 2xl:text-[15px] text-text-secondary hover:bg-bg-hover transition-colors"
                  >
                    <Gift className="h-4 w-4 2xl:h-[18px] 2xl:w-[18px]" />
                    {t('topbar.referrals', 'برنامج الإحالة')}
                  </button>

                  {/* Switch store */}
                  <button
                    onClick={() => { navigate('/'); setShowUserMenu(false); }}
                    className="flex w-full items-center gap-2.5 px-4 py-2 text-sm 2xl:text-[15px] text-text-secondary hover:bg-bg-hover transition-colors"
                  >
                    <Store className="h-4 w-4 2xl:h-[18px] 2xl:w-[18px]" />
                    {t('topbar.switchStore', 'تبديل المتجر')}
                  </button>
                </div>

                <div className="border-t border-border-default/50 py-1.5">
                  {/* Language toggle */}
                  <button
                    onClick={() => { toggleLang(); setShowUserMenu(false); }}
                    className="flex w-full items-center gap-2.5 px-4 py-2 text-sm 2xl:text-[15px] text-text-secondary hover:bg-bg-hover transition-colors"
                  >
                    <Globe className="h-4 w-4 2xl:h-[18px] 2xl:w-[18px]" />
                    {i18n.language === 'ar' ? 'English' : 'العربية'}
                  </button>
                </div>

                <div className="border-t border-border-default/50 py-1.5">
                  {/* Logout */}
                  <button
                    onClick={logout}
                    className="flex w-full items-center gap-2.5 px-4 py-2 text-sm 2xl:text-[15px] text-danger hover:bg-bg-hover transition-colors"
                  >
                    <LogOut className="h-4 w-4 2xl:h-[18px] 2xl:w-[18px]" />
                    {t('topbar.logout', 'تسجيل الخروج')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Search / Command Palette */}
      {showSearch && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] px-4"
          onClick={() => setShowSearch(false)}
        >
          <div className="fixed inset-0 bg-black/50" />
          <div
            className="relative w-full max-w-lg 2xl:max-w-xl rounded-xl border border-border-default bg-bg-secondary shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 px-4 2xl:px-5 border-b border-border-default/50">
              <Search className="h-4 w-4 2xl:h-5 2xl:w-5 text-text-muted shrink-0" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('common.search') + '...'}
                className="flex-1 bg-transparent py-3.5 2xl:py-4 text-sm 2xl:text-base text-text-primary outline-none placeholder:text-text-muted"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearchSubmit();
                }}
              />
              <kbd className="text-[10px] text-text-muted/60 border border-border-default/60 rounded px-1.5 py-0.5 font-mono">ESC</kbd>
            </div>
            <div className="max-h-80 2xl:max-h-96 overflow-y-auto py-2">
              {filteredSearch.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleSearchSelect(item)}
                  className="flex w-full items-center gap-3 px-4 2xl:px-5 py-2.5 2xl:py-3 text-start text-sm 2xl:text-[15px] hover:bg-bg-hover transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-text-primary truncate">{item.label}</div>
                    {item.parent && (
                      <div className="text-text-muted text-xs 2xl:text-sm truncate">{item.parent}</div>
                    )}
                  </div>
                  {item.shortcut && (
                    <kbd className="text-[10px] 2xl:text-[11px] text-text-muted/50 border border-border-default/50 rounded px-1.5 py-0.5 font-mono shrink-0">⌘{item.shortcut}</kbd>
                  )}
                </button>
              ))}
              {searchQuery.trim() && (
                <button
                  onClick={() => {
                    navigate(`/${storeId}/products?q=${encodeURIComponent(searchQuery.trim())}`);
                    setShowSearch(false);
                    setSearchQuery('');
                  }}
                  className="flex w-full items-center gap-3 px-4 2xl:px-5 py-2.5 2xl:py-3 text-start text-sm 2xl:text-[15px] hover:bg-bg-hover transition-colors border-t border-border-default/30"
                >
                  <Search className="h-4 w-4 text-accent shrink-0" />
                  <span className="text-accent">{t('common.search')} "{searchQuery.trim()}" {t('sidebar.products')}</span>
                </button>
              )}
              {filteredSearch.length === 0 && !searchQuery.trim() && (
                <p className="px-4 py-6 text-sm 2xl:text-base text-text-muted text-center">{t('common.noResults')}</p>
              )}
            </div>
          </div>
        </div>
      )}

      <MyAccountModal
        isOpen={showAccountModal}
        onClose={() => setShowAccountModal(false)}
      />
    </>
  );
}
